import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

// Define UI order for different chat types.
// GraphQL enums don't preserve order, so we define it explicitly here.
export const ORDERED_CHAT_TYPES = [ChatType.DEEP_RESEARCH, ChatType.DATA_CAPTURE];

export function isAISidepanelEnabled(): boolean {
  return getChatTypes().length > 0;
}

export function getChatTypes(): Array<ChatType> {
  return ORDERED_CHAT_TYPES.filter((chatType) => isChatTypeEnabled(chatType));
}

export function getDefaultChatType(): ChatType {
  return getChatTypes()[0] ?? ChatType.DEEP_RESEARCH;
}

export function isChatTypeEnabled(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case ChatType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    default:
      return false;
  }
}

/**
 * Parse the value out of a URL like/chats/new?agent=<agentTypeQSValue> (see: AIRoutes.ts).
 *
 * NOTE: These URLs may be put in marketing emails, so we should avoid changing the string values
 * when possible, i.e. maintain backwards compatibility. It is acceptable to have multiple querystring
 * values map to the same AgentType.
 */
export function getAgentTypeFromQueryStringValue(agentTypeQSValue: string | null): AgentType | null {
  switch (agentTypeQSValue) {
    case 'ask':
      return AgentType.ASK;
    case 'deep-research':
      return AgentType.DEEP_RESEARCH;
    case 'compose':
      return AgentType.DATA_CAPTURE;
    default:
      return null;
  }
}

export function getChatTypeText(chatType: ChatType): string {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return 'deep research';
    case ChatType.DATA_CAPTURE:
      return 'Compose';
    default:
      throw new Error(`Unknown chat type: ${assertNever(chatType)}`);
  }
}

export function doesChatTypePreferExpandedSidepanel(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DATA_CAPTURE:
      return true;
    case ChatType.DEEP_RESEARCH:
      return false;
    default:
      assertNever(chatType);
      return false;
  }
}

export const ChatTypeByAgentType: Record<AgentType, ChatType> = {
  [AgentType.DEEP_RESEARCH]: ChatType.DEEP_RESEARCH,
  [AgentType.ASK]: ChatType.DEEP_RESEARCH,
  [AgentType.DATA_CAPTURE]: ChatType.DATA_CAPTURE,
};

export function getAgentTypeIcon(agentType: AgentType): BenchlingIconName | null {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'file-magnifying-glass';
    case AgentType.DATA_CAPTURE:
      return 'pen';
    case AgentType.ASK:
      return 'comments';
    default:
      assertNever(agentType);
      return null;
  }
}

export function getAgentTypeText(agentType: AgentType): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Deep Research';
    case AgentType.ASK:
      return 'Ask';
    case AgentType.DATA_CAPTURE:
      return 'Compose';
    default:
      assertNever(agentType);
      throw new Error(`Unknown agent type: ${assertNever(agentType)}`);
  }
}

export function isAgentTypeEnabled(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentType.ASK:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    default:
      assertNever(agentType);
      return false;
  }
}

export function getAvailableAgentTypes(chatTypes: Array<ChatType>): Array<AgentType> {
  // When agent switching is enabled, all enabled agents are available regardless of the current
  // chat type. This allows users to switch between agents (e.g., Deep Research -> Compose) within
  // the same thread. Backend compatibility logic handles the different invariants each agent has.
  if (BenchlingConfig.LLM_ENABLE_AGENT_SWITCH) {
    return Object.values(AgentType).filter((agentType) => isAgentTypeEnabled(agentType));
  }
  return Object.values(AgentType).filter(
    (agentType) => isAgentTypeEnabled(agentType) && chatTypes.includes(ChatTypeByAgentType[agentType])
  );
}

export function getAgentTypePlaceholderText(agentType: AgentType): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Write detailed reports based on your data';
    case AgentType.ASK:
      return 'Ask questions about your data';
    case AgentType.DATA_CAPTURE:
      return 'Draft an entry or template';
    default:
      assertNever(agentType);
      return '';
  }
}

/**
 * doesAgentTypeSupportSources must also return true to allow public data to be used.
 */
export function doesAgentTypeSupportPublicData(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA;
    case AgentType.ASK:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent backend supports setting CreateDisplayMessageChatEvent['sources'].
 */
export function doesAgentTypeSupportSources(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return true;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.ASK:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the chat type should show progress UI (tool calls, activity tracking).
 *
 * TODO: This should be unconditionally true.
 */
export function doesChatTypeShowProgress(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return true;
    case ChatType.DATA_CAPTURE:
      return true;
    default:
      assertNever(chatType);
      return false;
  }
}
